/**
 * ConversationMembersTab
 * Lista de membros de um grupo — pesquisa client-side, badge de papel, exportação CSV
 */

import React, { useState, useMemo } from 'react';
import type { ConversationResponse } from '@troia-v3/types';
import { Input } from '@/shared/components/ui/Input';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Icon } from '@/shared/icons';

interface ConversationMembersTabProps {
  conversation: ConversationResponse;
}

interface Member {
  id: string;
  name: string;
  picture?: string;
  phone?: string;
  role: 'admin' | 'member';
  joinedAt: string;
}

function exportCsv(members: Member[], groupName: string): void {
  const BOM = '\uFEFF';
  const headers = ['Nome', 'Telefone', 'Papel', 'Entrou em'];
  const rows = members.map((m) => [
    m.name,
    m.phone ?? '',
    m.role === 'admin' ? 'Admin' : 'Membro',
    new Date(m.joinedAt).toLocaleDateString('pt-BR'),
  ]);
  const csvContent =
    BOM +
    [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `membros-${groupName.replace(/\s+/g, '-')}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const ConversationMembersTab: React.FC<ConversationMembersTabProps> = ({ conversation }) => {
  const [search, setSearch] = useState('');
  const members: Member[] = (conversation.members ?? []) as Member[];
  const groupName = conversation.group?.name ?? 'grupo';

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return members;
    return members.filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        (m.phone ?? '').toLowerCase().includes(term)
    );
  }, [members, search]);

  return (
    <div className="flex flex-col h-full">
      {/* Search + Export */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-2 flex-shrink-0">
        <div className="flex-1">
          <Input
            placeholder="Pesquisar membro..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() => exportCsv(members, groupName)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
          style={{
            backgroundColor: '#f1f5f9',
            color: '#475569',
            border: '1px solid #e5e7eb',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e2e8f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
          }}
          title="Exportar CSV"
        >
          <Icon name="download" className="w-4 h-4" />
          CSV
        </button>
      </div>

      {/* Member list */}
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Icon name="users" className="w-8 h-8" style={{ color: '#cbd5e1' }} />
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              {search ? 'Nenhum membro encontrado' : 'Nenhum membro neste grupo'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filtered.map((member, idx) => (
              <div
                key={member.id ?? idx}
                className="flex items-center gap-3 p-2.5 rounded-lg"
                style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : '#f8fafc' }}
              >
                <Avatar className="w-9 h-9 flex-shrink-0" isSquircle>
                  <AvatarImage src={member.picture} alt={member.name} isSquircle />
                  <AvatarFallback isSquircle>
                    <span className="text-xs font-semibold" style={{ color: '#8b5cf6' }}>
                      {member.name.charAt(0)}
                    </span>
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                    {member.name}
                  </p>
                  {member.phone && (
                    <p className="text-xs truncate" style={{ color: '#94a3b8' }}>
                      {member.phone}
                    </p>
                  )}
                </div>
                <span
                  className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={
                    member.role === 'admin'
                      ? { backgroundColor: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }
                      : { backgroundColor: '#f1f5f9', color: '#64748b' }
                  }
                >
                  {member.role === 'admin' ? 'Admin' : 'Membro'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className="flex-shrink-0 px-4 py-2 border-t text-xs flex items-center justify-between"
        style={{ borderColor: '#e5e7eb', color: '#94a3b8' }}
      >
        <span>
          {filtered.length} de {members.length} membro{members.length !== 1 ? 's' : ''}
          {search && ` (filtrado)`}
        </span>
        {members.filter((m) => m.role === 'admin').length > 0 && (
          <span>
            {members.filter((m) => m.role === 'admin').length} admin
            {members.filter((m) => m.role === 'admin').length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default ConversationMembersTab;
